import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { MONTHS, YEARS, todayParts } from "../../constants";
import { PageTitle, Select, Spinner, Empty, DataCard, Icon, cx } from "../../components/ui";

// Warna sel = tier gaji berdasar jumlah HADIR: 1-5 merah, 6-7 kuning, 8-10 hijau,
// 11+ biru. 0 (kelas tak berjalan / tak ada yang hadir) → netral/abu.
function cellStyle(n) {
  if (!n || n <= 0) return "bg-slate-50 text-slate-300 dark:bg-slate-800/40 dark:text-slate-600";
  if (n <= 5) return "bg-red-500 text-white";
  if (n <= 7) return "bg-amber-400 text-slate-900";
  if (n <= 10) return "bg-green-500 text-white";
  return "bg-blue-500 text-white";
}

const WD = ["M", "S", "S", "R", "K", "J", "S"]; // Min..Sab (indikator akhir pekan)

export default function ManagementDailyPage({ user, role = null, setToast }) {
  const today = todayParts();
  const [filters, setFilters] = useState({ year: today.year, month: today.month });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cakupan cabang mengikuti PORTAL yang sedang dibuka (role aktif): admin/management/super
  // → SEMUA; Team Leader → HANYA cabangnya. Backend juga men-scope via token, tapi saat
  // AUTH_ENFORCE mati (lokal) token bisa tak resolve → filter di sini pakai user.branches.
  //
  // Dulu ditentukan dari teks `position`. Akun trainer yang juga punya role management
  // tetap ber-position "Regular Trainer", jadi di Management Portal ia tersaring ke cabang
  // tempatnya mengajar (mis. PNK saja) padahal tugasnya memantau semua cabang. `position`
  // hanya cadangan untuk akun lama yang belum punya daftar role.
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const peranAktif = role || roles[0] || "";
  const isFullAccess =
    !!user?.isSuperAdmin ||
    peranAktif === "admin" ||
    peranAktif === "management" ||
    (!peranAktif && /admin|management/i.test(user?.position || ""));
  const allowedIds = isFullAccess
    ? null
    : new Set((user?.branches || []).map((b) => b.id));

  async function load() {
    setLoading(true);
    try {
      const res = await api.getManagementDaily({
        year: filters.year,
        month: MONTHS.indexOf(filters.month) + 1
      });
      setData(res);
    } catch (err) {
      setToast && setToast({ type: "error", message: err.message || "Gagal memuat data" });
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [filters.month, filters.year]);

  // Cabang yang boleh dilihat pemanggil (Team Leader → cabangnya saja).
  const visibleBranches = (data?.branches || []).filter(
    (b) => !allowedIds || allowedIds.has(b.id)
  );

  const days = data?.daysInMonth
    ? Array.from({ length: data.daysInMonth }, (_, i) => i + 1)
    : [];
  const yearNum = Number(filters.year);
  const monthNum = MONTHS.indexOf(filters.month) + 1;
  const isWeekend = (d) => {
    const wd = new Date(yearNum, monthNum - 1, d).getDay();
    return wd === 0 || wd === 6;
  };

  return (
    <>
      <PageTitle
        title="Absensi Harian per Cabang"
        subtitle={`Monitoring kehadiran murid tiap kelas — ${filters.month} ${filters.year}.`}
        action={
          <div className="flex flex-wrap gap-2">
            <Select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
              className="w-28"
            >
              {YEARS.map((y) => <option key={y}>{y}</option>)}
            </Select>
            <Select
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              className="w-40"
            >
              {MONTHS.map((m) => <option key={m}>{m}</option>)}
            </Select>
          </div>
        }
      />

      <Legend />

      {loading ? (
        <Spinner label="Memuat absensi..." />
      ) : !data?.success || !visibleBranches.length ? (
        <Empty label="Belum ada data cabang." />
      ) : (
        <div className="space-y-8">
          {visibleBranches.map((b) => (
            <BranchTable
              key={b.id}
              branch={b}
              days={days}
              isWeekend={isWeekend}
              yearNum={yearNum}
              monthNum={monthNum}
            />
          ))}
        </div>
      )}
    </>
  );
}

function Legend() {
  const items = [
    ["1–5", "bg-red-500"],
    ["6–7", "bg-amber-400"],
    ["8–10", "bg-green-500"],
    ["11+", "bg-blue-500"]
  ];
  return (
    <div className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
      <span className="font-bold uppercase tracking-wide">Jumlah hadir:</span>
      {items.map(([label, color]) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span className={cx("inline-block h-3.5 w-3.5 rounded", color)} />
          {label}
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded bg-rose-500/15 text-[9px] font-black leading-none text-rose-600 ring-1 ring-inset ring-rose-400/50">0</span>
        Diabsen, 0 hadir
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3.5 w-3.5 rounded bg-slate-100 dark:bg-slate-800/40" />
        Belum diabsen
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="relative inline-block h-3.5 w-3.5 rounded bg-slate-200 dark:bg-slate-700">
          <span className="absolute right-0 top-0 h-1.5 w-1.5 rounded-full bg-violet-500 ring-1 ring-white dark:ring-slate-900" />
        </span>
        Diisi trainer pengganti
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="relative inline-block h-3.5 w-3.5 rounded bg-slate-200 dark:bg-slate-700">
          <span className="absolute bottom-0 left-0 text-[8px] font-black leading-none text-amber-600">+</span>
        </span>
        Ada murid dari jadwal lain
      </span>
    </div>
  );
}

function BranchTable({ branch, days, isWeekend, yearNum, monthNum }) {
  // Baris = KELAS. Label "trainer · sesi" bisa kembar bila satu trainer punya dua kelas di
  // jam yang sama; nama kelasnya jadi pembeda, hanya di baris yang memang kembar.
  const jumlahLabel = new Map();
  branch.classes.forEach((c) => jumlahLabel.set(c.label, (jumlahLabel.get(c.label) || 0) + 1));
  const jumlahKelas = branch.classes.filter((c) => !c.tanpaKelas).length;
  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <Icon name="store" className="text-primary" />
        <h3 className="text-lg font-black">
          {branch.code}
          {branch.name ? <span className="text-slate-400 font-medium"> · {branch.name}</span> : null}
        </h3>
        <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
          {jumlahKelas} kelas
        </span>
      </div>

      {branch.classes.length === 0 ? (
        <Empty label="Belum ada data absensi bulan ini." />
      ) : (
        <DataCard>
          {/* Tinggi tabel dibatasi → banyak kelas cukup di-scroll (vertikal & horizontal),
              tak memanjang menghabiskan layar. Header baris tetap menempel saat scroll. */}
          <div className="max-h-[460px] overflow-auto">
            <table className="w-full border-collapse text-center text-xs">
              <thead>
                <tr>
                  <th className="sticky left-0 top-0 z-30 bg-slate-100 px-3 py-2 text-left text-[11px] font-black uppercase tracking-wider text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    Kelas
                  </th>
                  {days.map((d) => (
                    <th
                      key={d}
                      className={cx(
                        "sticky top-0 z-10 min-w-[30px] px-1 py-2 font-black",
                        isWeekend(d)
                          ? "bg-slate-200 text-slate-400 dark:bg-slate-800/80"
                          : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
                      )}
                    >
                      <div className="leading-none">{d}</div>
                      <div className="text-[8px] font-medium opacity-60">
                        {WD[new Date(yearNum, monthNum - 1, d).getDay()]}
                      </div>
                    </th>
                  ))}
                  <th className="sticky right-0 top-0 z-30 bg-slate-100 px-2 py-2 font-black text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    Tot
                  </th>
                </tr>
              </thead>
              <tbody>
                {branch.classes.map((c, i) => (
                  <tr key={c.classId ?? `t${i}`} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-3 py-1.5 text-left font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                      <div className="text-[13px]">{c.trainer}</div>
                      <div className="text-[10px] font-medium text-slate-400">
                        {c.session}
                        {c.name && jumlahLabel.get(c.label) > 1 ? ` · ${c.name}` : ""}
                      </div>
                      {/* Bulan itu kelas ini dipegang trainer lain — sebelum diserahkan ke
                          trainer sekarang, atau sebelum data kelas ada. Tanpa ini barisnya
                          terbaca seolah trainer yang sekarang yang mengajar. */}
                      {!!(c.pengajarLain || []).length && (
                        <div
                          title="Bulan ini kelas ini juga diajar trainer lain sebagai pemegangnya (bukan pengganti) — mis. sebelum kelasnya diserahkan ke trainer sekarang."
                          className="text-[10px] font-semibold text-amber-600 dark:text-amber-400"
                        >
                          diajar {c.pengajarLain.join(", ")}
                        </div>
                      )}
                      {/* Absensi yang tak punya kelas: muridnya sudah keluar dari semua kelas
                          di jam itu dan trainernya tak punya kelas di jam itu. Tetap dihitung
                          supaya angkanya tak hilang, tapi jelas bukan kelas yang berjalan. */}
                      {c.tanpaKelas && (
                        <span
                          title="Murid-muridnya tak lagi terdaftar di kelas mana pun pada jam ini, dan trainer ini tak punya kelas di jam ini."
                          className="mt-0.5 inline-block rounded bg-slate-500/10 px-1.5 py-0.5 text-[9px] font-black text-slate-500"
                        >
                          tanpa kelas
                        </span>
                      )}
                    </td>
                    {c.days.map((n, di) => {
                      // Bedakan: 0 hadir TAPI sudah diabsen vs BELUM diabsen (sama-sama 0).
                      const done = (c.done || [])[di];
                      const absentDone = n <= 0 && done;
                      // Kelas hari ini diisi trainer PENGGANTI (bukan trainer asli).
                      const cover = (c.cover || [])[di];
                      // Berapa dari angka itu yang murid jadwal lain. Angka gabungan tanpa
                      // keterangan bikin admin mengira kelasnya membengkak sendiri.
                      const tambah = (c.tambahan || [])[di] || 0;
                      const base =
                        n > 0
                          ? `Tanggal ${di + 1}: ${n} hadir${tambah ? ` (${tambah} di antaranya murid dari jadwal lain)` : ""}`
                          : absentDone
                          ? `Tanggal ${di + 1}: sudah diabsen, 0 murid hadir`
                          : `Tanggal ${di + 1}: belum diabsen`;
                      return (
                        <td key={di} className="p-0.5">
                          <div
                            className={cx(
                              "relative flex h-8 items-center justify-center rounded font-bold",
                              n > 0
                                ? cellStyle(n)
                                : absentDone
                                ? "bg-rose-500/15 text-rose-600 ring-1 ring-inset ring-rose-400/50 dark:text-rose-400"
                                : "bg-slate-50 text-slate-300 dark:bg-slate-800/40 dark:text-slate-600"
                            )}
                            title={cover ? `${base} · diisi ${cover} (pengganti)` : base}
                          >
                            {n > 0 ? n : absentDone ? "0" : ""}
                            {tambah > 0 && (
                              <span
                                className="absolute bottom-0 left-0.5 text-[9px] font-black leading-none text-amber-600 dark:text-amber-400"
                                aria-label={`${tambah} murid dari jadwal lain`}
                              >
                                +{tambah}
                              </span>
                            )}
                            {cover && (
                              <span
                                className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-violet-500 ring-1 ring-white dark:ring-slate-900"
                                aria-label={`Diisi ${cover} (pengganti)`}
                              />
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-10 bg-white px-2 py-1.5 font-black text-primary dark:bg-slate-900">
                      {c.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DataCard>
      )}
    </div>
  );
}
